import { AfterViewInit, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalizeRouterService } from 'localize-router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { constants } from 'src/app/config/constants';
import { UserFactory } from './core/user.factory';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../environments/environment';
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";
import { CookieService } from 'angular2-cookie/services/cookies.service';
import { AuthenticationService } from './core/authentication.service';
import * as moment from 'moment';
import { TranstationFactory } from './core/translation.factory';
import Swal from 'sweetalert2';
import { SessionTimeoutService } from 'src/app/services/session-timeout.service';

const DATE_FORMAT_DEFAULT = 'DD MMM YYYY';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {

  constructor(
    public translate: TranslateService,
    public localize: LocalizeRouterService,
    public ngxLoaderService: NgxUiLoaderService,
    public userFactory: UserFactory,
    public http: HttpClient,
    public idle: Idle,
    public keepalive: Keepalive,
    private cookieService: CookieService,
    private authService: AuthenticationService,
    private transtationFactory: TranstationFactory,
    private sessionTimeoutService: SessionTimeoutService
  ) {
    // sets an idle timeout of 5 seconds, for testing purposes.
    this.idle.setIdle(5);
    // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
    this.idle.setTimeout((env.IdleTimeoutCountdownMin * 60));
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart.subscribe(() => {
      console.log('You have gone Idle');
    });

    this.idle.onIdleEnd.subscribe(() => {
      console.log('No Longer Idle');
    });

    this.idle.onTimeoutWarning.subscribe((countdown) => {
      //console.log('You will time out in ' + countdown + ' seconds');
    });

    this.idle.onTimeout.subscribe(() => {
      if (Swal.isVisible()) {
        Swal.close();
      }

      let timerInterval;
      Swal.fire({
        title: this.transtationFactory.get('idle_warning_title'),
        html: this.transtationFactory.get('idle_warning_html'),
        type: 'warning',
        timer: (env.IdleTimeoutWarningMin * 60 * 1000),
        showConfirmButton: true,
        confirmButtonText: this.transtationFactory.get('button.confirm'),
        showCancelButton: true,
        cancelButtonText: this.transtationFactory.get('button.logout'),
        onOpen: () => {
          const timer = Swal.getPopup().querySelector("b");
          timerInterval = setInterval(() => {
            let timeleft = Math.floor(Swal.getTimerLeft());
            let seconds = Math.floor((timeleft / 1000) % 60);
            let minutes = Math.floor((timeleft / 1000 / 60) % 60);
            timer.textContent = `${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`;
          }, 100);
        },
        onClose: () => {
          clearInterval(timerInterval);
        }
      }).then((result) => {
        if (result.value === true) {
          //Refresh Token
          console.log('Refresh Token');

          if (this.userFactory.getToken() !== null) {
            this.http.post<any>(env.apiEndpoint + '/auth/refreshToken', {
              refreshToken: this.userFactory.getRefreshToken()
            }).subscribe(res => {
              this.userFactory.saveToken(res.accessToken);
              this.userFactory.saveRefreshToken(res.refreshToken);

              this.sessionTimeoutService.stopTokenExpiredClock();
              this.http.post<any>(env.apiEndpoint + '/auth/getTokenExpiredDate', {}).subscribe(response => {
                this.sessionTimeoutService.startTokenExpiredClock(response);
              });
            });
            this.resetTimeout();
          }
        }
        else if (result.dismiss === Swal.DismissReason.cancel) {
          this.logout();
        }
        else if (result.dismiss === Swal.DismissReason.timer) {
          this.logout();
        }
        else {
          this.resetTimeout();
        }
      });
    });

    this.translate.addLangs(constants.languageOptions);
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(constants.language);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    const browserLang = this.translate.getBrowserLang();
    // translate.use(browserLang.match(/en|th/) ? browserLang : constants.language);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.localize.init();

    const lang = this.userFactory.getCurrentLang() ? this.userFactory.getCurrentLang() : constants.language;
    this.translate.use(lang);
    this.localize.changeLanguage(lang);

    //console.log('env.isSso', env.isSso);

    /*Check token is expired.*/
    if (env.isSso) {
      if (this.userFactory.getToken() !== null) {
        this.authService.getAgentProfile().subscribe(res => {
          const profile = res['agentProfileDetail'];
          this.userFactory.saveProfile(profile.agentId);
          this.checkExpireDate(res);
        });
      }
      // const ssoToken = JSON.stringify(this.cookieService.get('SESSION_ID'));
      // console.log('ssotoken: ',ssoToken);
      // console.log('cookie: ',JSON.stringify(this.cookieService.get('SESSION_ID')));

      // if (ssoToken != null) {
      //   // this.userFactory.saveToken(this.substringToken(ssoToken));
      //   this.authService.getAgentProfile().subscribe(res => {
      //     const profile = res['agentProfileDetail'];
      //     this.userFactory.saveProfile(profile.agentId);
      //     this.checkExpireDate(res);
      //   });
      //   this.cookieService.remove('SESSION_ID');
      // }
    } else {
      if (this.userFactory.getToken() !== null) {
        this.http.post<any>(env.apiEndpoint + '/auth/verifyToken', {}).subscribe(res => {
          this.userFactory.saveToken(res.token);
        });
      }
    }
  }

  resetTimeout() {
    //console.log('resetTimeout');

    this.idle.watch();
  }

  logout() {
    this.authService.logoutJwt().subscribe(res => {
      this.idle.stop();
      this.authService.logout();
    });
  }

  checkExpireDate(res) {
    if (res['isPass'] === 'Y') {
      const nowDate = moment(new Date()).startOf('day');
      const date = new Date(res.agentProfileDetail.licenseExpired);
      let expireDate = moment(date);
      const years = expireDate.diff(nowDate, 'years');
      const months = expireDate.diff(nowDate, 'months') - (years * 12);
      const days = expireDate.diff(nowDate, 'days');

      expireDate = this.translate.currentLang === 'th' ? expireDate.add(543, 'year') : expireDate;
      const dateStr = expireDate.format(DATE_FORMAT_DEFAULT);

      const massage = this.transtationFactory.get('expire_warning_text')
        + days + this.transtationFactory.get('expire_warning_text_day')
        + dateStr;

      if (days <= 60) {
        Swal.fire({
          title: this.transtationFactory.get('expire_warning'),
          text: massage,
          type: 'warning',
          confirmButtonText: this.transtationFactory.get('button.close')
        });
      }
    }
  }

  ngOnInit(): void {
    this.ngxLoaderService.start();
  }

  ngAfterViewInit(): void {
    this.ngxLoaderService.stop();
  }

  substringToken(token: string): string {
    if (token) {
      return token.replace(/["]/g, '').trim();
    }
    return '';
  }

}
